import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { createApiRequest } from '../../utils/apiUtils';
import Header from '../../Components/Header/Header';
import { Container, Box, Typography, Alert, CircularProgress, Button, Stack } from '@mui/material';

/**
 * state ペイロードから storeId を取り出す。
 * state は BE が生成した `base64url(storeId|epoch|nonce) + "." + 署名` 形式。
 * 改ざん検証は BE の署名検証が最終防衛であり、ここでのデコードは画面遷移（表示用途）のみに使う。
 */
const extractStoreIdFromState = (state: string): string | null => {
  try {
    const payload = state.split('.')[0];
    // base64url → base64（パディング補完）
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = window.atob(padded);
    const storeId = decoded.split('|')[0];
    return storeId || null;
  } catch {
    return null;
  }
};

const extractMessage = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw);
    if (j && typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw || '連携処理に失敗しました。';
};

/**
 * Stripe Connect OAuth コールバックページ。feast-fe ADR 0039。
 * Stripe の redirect URI は完全一致の事前登録制のため、店舗 ID を含まない固定パス
 * /stripe/connect/callback で受け、state から storeId を取り出して
 * GET /stores/{storeId}/stripe/callback?code=...&state=... へ中継する（state 検証・トークン交換は BE）。
 */
const StripeConnectCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState<string | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);
  // React 18 StrictMode の開発時二重実行で認可コード交換（1 回限り）を二重送信しないためのガード。
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    // ユーザーが Stripe 側で連携を拒否した場合などは error / error_description が返る。
    const oauthError = searchParams.get('error');

    const sid = state ? extractStoreIdFromState(state) : null;
    setStoreId(sid);

    if (oauthError) {
      setStatus('error');
      setMessage(searchParams.get('error_description') || `Stripe との連携が完了しませんでした（${oauthError}）。`);
      return;
    }
    if (!code || !state || !sid) {
      setStatus('error');
      setMessage('連携情報が不正です。お手数ですが、店舗管理画面からやり直してください。');
      return;
    }

    createApiRequest<void>(
      `/stores/${sid}/stripe/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
      { method: 'GET' },
    )
      .then(() => setStatus('success'))
      .catch((e) => {
        setStatus('error');
        setMessage(extractMessage(e));
      });
  }, [searchParams]);

  // 成功時は数秒後に店舗管理画面へ自動で戻る（手動リンクも併設）。
  useEffect(() => {
    if (status !== 'success' || !storeId) return;
    const timer = setTimeout(() => navigate(`/${storeId}/manage/store`), 3000);
    return () => clearTimeout(timer);
  }, [status, storeId, navigate]);

  const managePath = storeId ? `/${storeId}/manage/store` : '/home';

  return (
    <>
      <Header />
      <Container component="main" maxWidth="sm">
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Stripe 連携
          </Typography>
          {status === 'processing' && (
            <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Stripe との連携を処理しています。このままお待ちください…
              </Typography>
            </Stack>
          )}
          {status === 'success' && (
            <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
              <Alert severity="success" sx={{ width: '100%' }}>
                連携が完了しました。まもなく店舗管理画面へ戻ります。
              </Alert>
              <Button component={RouterLink} to={managePath} variant="contained" size="small">
                店舗管理画面へ戻る
              </Button>
            </Stack>
          )}
          {status === 'error' && (
            <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
              <Alert severity="error" sx={{ width: '100%' }}>
                {message || 'Stripe との連携に失敗しました。'}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                店舗管理画面の「Stripeと連携する」からやり直せます。
              </Typography>
              <Button component={RouterLink} to={managePath} variant="outlined" size="small">
                店舗管理画面へ戻る
              </Button>
            </Stack>
          )}
        </Box>
      </Container>
    </>
  );
};

export default StripeConnectCallback;
